import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import PaperTooltip from '../BaseComponents/PaperToolTip';
import { classNames, checkPermissionOnElement } from '../../services/utils';
import {
  TIMELINE_STAGES,
  TIMELINE_STATUS,
  FAILURE,
  IN_PROGRESS,
  COMPLETED,
  ERROR,
  LOCKED,
  CONDITIONAL_STATUS
} from '../../constants/statusTimelineConstants';
import WaitingIcon from '../../assets/icons/waitingIcon.svg';
import LockIcon from '../../assets/icons/lockIcon.svg';
import Success from '../../assets/icons/successIcon.svg';
import ErrorIcon from '../../assets/icons/errorIcon.svg';
import FailureIcon from '../../assets/icons/failureIcon.svg';

const Lock = () => (
  <span className="status-lock">
    <LockIcon />
  </span>
);

const Waiting = () => (
  <span className="btn-floating status-waiting pulse">
    <WaitingIcon />
  </span>
);

export default function StatusTimeline({
  className: classNameProp,
  cleverTapEvent,
  contentClassName,
  location,
  onBoardingHost,
  careHost,
  timelineStatus
}) {
  const {
    data: {
      timelineStatus: timeline,
      conditionalStatus
    },
    fetching,
    fetched,
  } = timelineStatus;
  let mainTimelineMsg;
  let isAllSuccess = true;
  let actionLink;

  const handleCtaClick = (ctaName) => (e) => {
    cleverTapEvent('Timeline CTA clicked', {
      'CTA Name': ctaName,
      source: location.pathname,
    });
  }

  function getConditionalData(stage, conditionalStatus) {
    let data;
    if (stage.conditional) {
      const condition = CONDITIONAL_STATUS[conditionalStatus];
      if (condition) {
        data = stage[condition];
        if (!data) {
          data = stage.default;
        }
      } else {
        data = stage.default;
      }
    } else {
      data = stage;
    }
    if (!mainTimelineMsg) {
      mainTimelineMsg = data.timelineMsg;
    }
    return {
      iconLabel: data.label,
      bottomLabel: data.stageMsg,
      link: data.link,
    };
  }

  function renderTimelineStages() {
    if (!Object.keys(timeline).length) return;
    const stageList = Object.entries(TIMELINE_STAGES);
    return stageList.map(([stageName, stageData], index) => {
      const { status, timestamp } = timeline[stageName];
      const stageStatus = TIMELINE_STATUS[status];
      let iconLabel;
      let bottomLabel;
      let tooltipMsg;
      let icon;
      let link;
      let hasError;
      switch (stageStatus) {
        case COMPLETED:
          iconLabel = stageData.success.label;
          bottomLabel = timestamp
            ? moment(timestamp).format('D MMMM YYYY')
            : '';
          icon = <Success className="success-icon" />;
          if (stageData.success.timelineMsg) {
            mainTimelineMsg = stageData.success.timelineMsg;
          }
          break;
        case IN_PROGRESS:
          ({ iconLabel, bottomLabel, link } = getConditionalData(
            stageData.inProgress,
            conditionalStatus[stageName]
          ));
          icon = <Waiting />;
          isAllSuccess = false;
          break;
        case FAILURE:
          ({ iconLabel, bottomLabel, link } = getConditionalData(
            stageData.failure,
            conditionalStatus[stageName]
          ));
          bottomLabel = timestamp
            ? moment(timestamp).format('D MMMM YYYY')
            : '';
          icon = <FailureIcon />;
          hasError = true;
          isAllSuccess = false;
          break;
        case ERROR:
          ({ iconLabel, bottomLabel, link } = getConditionalData(
            stageData.failure,
            conditionalStatus[stageName]
          ));
          bottomLabel = timestamp
            ? moment(timestamp).format('D MMMM YYYY')
            : '';
          icon = <ErrorIcon />;
          hasError = true;
          isAllSuccess = false;
          break;
        case LOCKED:
          ({ iconLabel, bottomLabel, link } = getConditionalData(
            stageData.locked,
            conditionalStatus[stageName]
          ));
          bottomLabel = null;
          tooltipMsg = stageData.locked.stageMsg;
          icon = <Lock />;
          isAllSuccess = false;
          break;
      }
      const labelClassName = classNames({
        'status-timeline--description-label': true,
        'status-timeline--error': hasError,
        'status-timeline--description-label--light': stageStatus === LOCKED
      });

      let stageDom = (
        <div className="status-timeline--list-item">
          <div className="status-timeline--icon">{icon}</div>
          <div className="status-timeline--description">
            <div className={labelClassName}>{iconLabel}</div>
            <div className="status-timeline--description-label-bottom">
              {bottomLabel}
            </div>

            {link &&
              link.to(onBoardingHost, careHost) !== location.pathname &&
              (
                <div className="status-timeline--cta">
                  {link.notADashboardRoute() ? (stageData.success.label === 'Profile completed' ?
                      (checkPermissionOnElement(link.permissionString, stageData) && <a
                      href={link.to(onBoardingHost, careHost)}
                      className="dflt-btn"
                      onClick={handleCtaClick(link.text)}
                      >
                        {link.text}
                      </a>)
                      : (<a
                        href={link.to(onBoardingHost, careHost)}
                        className="dflt-btn"
                        onClick={handleCtaClick(link.text)}
                      >
                      {link.text}
                      </a> )
                    ) : (stageData.success.label === 'Profile completed' ?
                      (checkPermissionOnElement(link.permissionString, stageData) && <Link
                      to={link.to(onBoardingHost, careHost)}
                      className="dflt-btn"
                      onClick={handleCtaClick(link.text)}
                      >
                        {link.text}
                      </Link>)
                      : (<Link
                        to={link.to(onBoardingHost, careHost)}
                        className="dflt-btn"
                        onClick={handleCtaClick(link.text)}
                      >
                        {link.text}
                      </Link> )
                    )}
                </div>
              )}
          </div>
        </div>
      );
      actionLink = actionLink || link;
      if (tooltipMsg) {
        stageDom = (
          <PaperTooltip
            content={<div dangerouslySetInnerHTML={{ __html: tooltipMsg }} />}
            wrapperClassName="m-0 status-timeline--tooltip"
            contentClassName="position-center"
          >
            {stageDom}
          </PaperTooltip>
        );
      }
      return React.Children.toArray([
        stageDom,
        index !== stageList.length - 1 && (
          <div className="status-timeline--list-stepper" />
        ),
      ]);
    });
  }

  const timeLineStages = renderTimelineStages();

  return (
    <div className={`card status-timeline ${classNameProp}`}>
      {fetching ? (
        <div className="loader" />
      ) : (
          <div className={contentClassName}>
            <p
              className={`status-timeline--msg ${
                isAllSuccess ? '' : 'status-timeline--error'
                }`}
            >
              {mainTimelineMsg}
            </p>
            <div className={`status-timeline--list ${actionLink ? 'vivid' : ''}`}>
              {timeLineStages}
            </div>
          </div>
        )}
    </div>
  );
}

StatusTimeline.propTypes = {
  careHost: PropTypes.string.isRequired,
  className: PropTypes.string,
  cleverTapEvent: PropTypes.func,
  contentClassName: PropTypes.string,
  onBoardingHost: PropTypes.string.isRequired,
  timelineStatus: PropTypes.shape({
    data: PropTypes.shape({
      timelineStatus: PropTypes.object,
      conditionalStatus: PropTypes.object,
    }),
  }).isRequired,
};

StatusTimeline.defaultProps = {
  careHost: '',
  className: '',
  cleverTapEvent: () => { },
  contentClassName: '',
  onBoardingHost: '',
};
